import React, { useMemo } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { getStatusChipColor } from 'app/utils/statusChipColors';
import {
  GuidanceTourStepCompletionMap,
  OnboardingStepAccessibilityMap,
} from '../types';
import { GUIDANCE_TOUR_DRAWER_Z_INDEX } from '../utils/guidanceTourLayoutInsets';
import {
  buildTourSectionProgress,
  TourSectionProgress,
  TourSectionStepEntry,
} from '../utils/tourSectionProgress';
import {
  getOnboardingProgressColor,
  getOnboardingStepIconColor,
  getOnboardingStepTextColor,
  getOnboardingPendingColor,
  OnboardingStepDisplayState,
  resolveOnboardingStepDisplayState,
} from '../utils/onboardingStepStatusColors';

const SECTION_PROGRESS_TOOLTIP_Z_INDEX = GUIDANCE_TOUR_DRAWER_Z_INDEX + 50;
export const GUIDANCE_TOUR_SECTION_PROGRESS_TOOLTIP_ATTR =
  'data-guidance-tour-section-progress-tooltip';
const SECTION_MIN_WIDTH_PX = 44;
const SEGMENT_HEIGHT_PX = 6;

interface GuidanceTourSectionProgressProps {
  steps: Parameters<typeof buildTourSectionProgress>[0];
  stepCompleted: GuidanceTourStepCompletionMap;
  stepAccessibility: OnboardingStepAccessibilityMap;
  stepIndex: number;
  onJumpToStep: (index: number) => void;
}

const StepStatusIcon: React.FC<{
  completed: boolean;
  isCurrent: boolean;
  displayState: OnboardingStepDisplayState;
}> = ({ completed, isCurrent, displayState }) => {
  const theme = useTheme();
  const iconSx = {
    fontSize: 14,
    color: getOnboardingStepIconColor(theme, displayState),
  };

  if (isCurrent) {
    return <PlayCircleOutlineIcon sx={iconSx} />;
  }
  if (completed) {
    return <CheckCircleIcon sx={iconSx} />;
  }
  return <RadioButtonUncheckedIcon sx={iconSx} />;
};

const TooltipStepRow: React.FC<{
  entry: TourSectionStepEntry;
  stepIndex: number;
  stepAccessibility: OnboardingStepAccessibilityMap;
  theme: Theme;
  onJumpToStep: (index: number) => void;
  listPaddingLeft?: number;
}> = ({
  entry,
  stepIndex,
  stepAccessibility,
  theme,
  onJumpToStep,
  listPaddingLeft = 0,
}) => {
  const { step, index, completed } = entry;
  const isCurrent = index === stepIndex;
  const accessibility = stepAccessibility[step.id];
  const isAccessible = accessibility ? accessibility.accessible : true;
  const displayState = resolveOnboardingStepDisplayState({
    completed,
    isAccessible,
  });
  const stepTextColor = getOnboardingStepTextColor(theme, displayState);

  return (
    <ListItemButton
      dense
      selected={isCurrent}
      disabled={!isAccessible}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (isAccessible) {
          onJumpToStep(index);
        }
      }}
      sx={{
        py: 0.125,
        px: 0.5,
        pl: listPaddingLeft > 0 ? listPaddingLeft : 0.5,
        minHeight: 22,
        borderRadius: 0.75,
        '&.Mui-selected': {
          bgcolor: 'action.selected',
        },
        '&.Mui-selected:hover': {
          bgcolor: 'action.selected',
        },
        '&:hover': {
          bgcolor: isAccessible ? 'action.hover' : undefined,
        },
      }}
    >
      <ListItemIcon sx={{ minWidth: 22, mr: 0.375, py: 0 }}>
        <StepStatusIcon
          completed={completed}
          isCurrent={isCurrent}
          displayState={displayState}
        />
      </ListItemIcon>
      <ListItemText
        disableTypography
        sx={{ my: 0, py: 0 }}
        primary={
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.75rem',
              lineHeight: 1.25,
              fontWeight: isCurrent ? 600 : 400,
              color: stepTextColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {step.title}
          </Typography>
        }
      />
    </ListItemButton>
  );
};

const SectionTooltipContent: React.FC<{
  section: TourSectionProgress;
  stepIndex: number;
  stepAccessibility: OnboardingStepAccessibilityMap;
  theme: Theme;
  onJumpToStep: (index: number) => void;
}> = ({ section, stepIndex, stepAccessibility, theme, onJumpToStep }) => {
  const successColor = getStatusChipColor(theme, 'SUCCESS');

  const subGroupHasBlockedStep = (entries: TourSectionStepEntry[]) =>
    entries.some(({ step }) => {
      const accessibility = stepAccessibility[step.id];
      return accessibility ? !accessibility.accessible : false;
    });

  const subGroupAllComplete = (entries: TourSectionStepEntry[]) =>
    entries.length > 0 && entries.every(({ completed }) => completed);

  return (
    <Box
      sx={{ px: 1.25, py: 1, minWidth: 250, maxWidth: 340 }}
      onClick={(event) => event.stopPropagation()}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 700,
            fontSize: '0.6875rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            lineHeight: 1.3,
            color: 'text.primary',
          }}
        >
          {section.groupName}
        </Typography>
        <Box
          sx={{
            px: 0.75,
            py: 0.125,
            borderRadius: 1,
            bgcolor:
              section.percent === 100
                ? alpha(successColor, theme.palette.mode === 'dark' ? 0.2 : 0.12)
                : section.percent > 0
                  ? alpha(
                      getOnboardingProgressColor(theme, section.percent) ?? successColor,
                      theme.palette.mode === 'dark' ? 0.2 : 0.12,
                    )
                  : alpha(theme.palette.text.primary, 0.06),
            flexShrink: 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6875rem',
              fontWeight: 700,
              color:
                section.percent === 100
                  ? getOnboardingStepTextColor(theme, 'completed')
                  : section.percent > 0
                    ? getOnboardingProgressColor(theme, section.percent) ?? 'text.primary'
                    : 'text.primary',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {section.percent}%
          </Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: 'block',
          mb: 0.75,
          color: 'text.secondary',
          fontSize: '0.6875rem',
        }}
      >
        {section.completedCount} of {section.totalCount} steps complete
      </Typography>

      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          pt: 0.5,
          maxHeight: 260,
          overflowY: 'auto',
        }}
      >
        {section.isFlatGroup ? (
          <List dense disablePadding>
            {section.stepEntries.map((entry) => (
              <TooltipStepRow
                key={entry.step.id}
                entry={entry}
                stepIndex={stepIndex}
                stepAccessibility={stepAccessibility}
                theme={theme}
                onJumpToStep={onJumpToStep}
              />
            ))}
          </List>
        ) : (
          section.subCategoryGroups.map((subGroup, subGroupIndex) => {
            const subGroupBlocked = subGroupHasBlockedStep(subGroup.stepEntries);
            const subGroupComplete = subGroupAllComplete(subGroup.stepEntries);
            const subGroupDisplayState: OnboardingStepDisplayState = subGroupComplete
              ? 'completed'
              : subGroupBlocked
                ? 'disabled'
                : 'pending';
            const subGroupTextColor = getOnboardingStepTextColor(
              theme,
              subGroupDisplayState,
            );

            return (
              <Box
                key={subGroup.name}
                sx={{ mb: subGroupIndex < section.subCategoryGroups.length - 1 ? 0.5 : 0 }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 0.25,
                    py: 0.125,
                    gap: 0.375,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1,
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      color: subGroupTextColor,
                      lineHeight: 1.2,
                    }}
                  >
                    {subGroup.name}
                  </Typography>
                  {subGroupComplete && (
                    <CheckCircleIcon
                      sx={{
                        fontSize: 13,
                        color: successColor,
                        flexShrink: 0,
                      }}
                    />
                  )}
                </Box>
                <List dense disablePadding sx={{ pl: 1 }}>
                  {subGroup.stepEntries.map((entry) => (
                    <TooltipStepRow
                      key={entry.step.id}
                      entry={entry}
                      stepIndex={stepIndex}
                      stepAccessibility={stepAccessibility}
                      theme={theme}
                      onJumpToStep={onJumpToStep}
                    />
                  ))}
                </List>
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

const getSegmentTrackColor = (theme: Theme) =>
  alpha(
    getOnboardingPendingColor(theme),
    theme.palette.mode === 'dark' ? 0.18 : 0.1,
  );

const getSectionLabelColor = (theme: Theme, section: TourSectionProgress) => {
  if (section.percent === 100) {
    return getOnboardingStepTextColor(theme, 'completed');
  }
  const progressColor = getOnboardingProgressColor(theme, section.percent);
  if (progressColor) {
    return progressColor;
  }
  if (section.isActive) {
    return getOnboardingStepTextColor(theme, 'pending');
  }
  return getOnboardingStepTextColor(theme, 'disabled');
};

const getSectionFillColor = (theme: Theme, percent: number) =>
  getOnboardingProgressColor(theme, percent) ?? getStatusChipColor(theme, 'SUCCESS');

const sectionTooltipSlotProps = {
  popper: {
    sx: { zIndex: SECTION_PROGRESS_TOOLTIP_Z_INDEX },
    modifiers: [
      {
        name: 'offset',
        options: { offset: [0, 8] },
      },
    ],
    [GUIDANCE_TOUR_SECTION_PROGRESS_TOOLTIP_ATTR]: 'true',
  },
  tooltip: {
    sx: (theme: Theme) => ({
      p: 0,
      maxWidth: 360,
      bgcolor: 'background.paper',
      color: 'text.primary',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1.5,
      boxShadow: theme.shadows[8],
    }),
    [GUIDANCE_TOUR_SECTION_PROGRESS_TOOLTIP_ATTR]: 'true',
  },
  arrow: {
    sx: {
      color: 'background.paper',
      '&::before': {
        border: 1,
        borderColor: 'divider',
      },
    },
  },
};

const GuidanceTourSectionProgress: React.FC<GuidanceTourSectionProgressProps> = ({
  steps,
  stepCompleted,
  stepAccessibility,
  stepIndex,
  onJumpToStep,
}) => {
  const theme = useTheme();
  const successColor = getStatusChipColor(theme, 'SUCCESS');
  const pendingTrackColor = getSegmentTrackColor(theme);
  const hoverTrackColor = alpha(
    getOnboardingPendingColor(theme),
    theme.palette.mode === 'dark' ? 0.28 : 0.16,
  );

  const sections = useMemo(
    () => buildTourSectionProgress(steps, stepCompleted, stepIndex),
    [steps, stepCompleted, stepIndex],
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        gap: 0,
        mt: 0.75,
        minWidth: 0,
        border: 1,
        borderColor: 'divider',
        borderRadius: 0,
        overflow: 'hidden',
      }}
    >
      {sections.map((section, index) => {
        const sectionLabelColor = getSectionLabelColor(theme, section);
        const isComplete = section.percent === 100;
        const isActive = section.isActive;
        const sectionFillColor = getSectionFillColor(theme, section.percent);

        return (
          <Tooltip
            key={section.groupName}
            title={
              <SectionTooltipContent
                section={section}
                stepIndex={stepIndex}
                stepAccessibility={stepAccessibility}
                theme={theme}
                onJumpToStep={onJumpToStep}
              />
            }
            placement="bottom"
            arrow
            enterDelay={120}
            leaveDelay={80}
            disableInteractive={false}
            slotProps={sectionTooltipSlotProps}
          >
            <Box
              component="button"
              type="button"
              aria-label={`${section.groupName}: ${section.percent}% complete`}
              aria-current={isActive ? 'step' : undefined}
              onClick={() => onJumpToStep(section.firstIncompleteStepIndex)}
              sx={{
                flex: `${section.totalCount} 1 0`,
                minWidth: SECTION_MIN_WIDTH_PX,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 0.25,
                px: 0,
                py: 0.375,
                m: 0,
                border: 'none',
                borderRight:
                  index < sections.length - 1 ? 1 : 0,
                borderColor: 'divider',
                borderRadius: 0,
                bgcolor: isActive
                  ? alpha(successColor, theme.palette.mode === 'dark' ? 0.08 : 0.04)
                  : 'background.paper',
                cursor: 'pointer',
                textAlign: 'inherit',
                font: 'inherit',
                color: 'inherit',
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  '& .section-progress-track': {
                    bgcolor: isComplete ? successColor : hoverTrackColor,
                  },
                },
                '&:focus-visible': {
                  outline: 'none',
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  boxShadow: `inset 0 0 0 1px ${alpha(successColor, 0.35)}`,
                },
              }}
            >
              <Box
                className="section-progress-track"
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: SEGMENT_HEIGHT_PX,
                  overflow: 'hidden',
                  borderRadius: 0,
                  bgcolor: isComplete ? successColor : pendingTrackColor,
                  transition: 'background-color 0.2s ease',
                }}
              >
                {!isComplete && section.percent > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: sectionFillColor,
                      transform: `scaleX(${section.percent / 100})`,
                      transformOrigin: 'left center',
                      transition: 'transform 0.25s ease, background-color 0.25s ease',
                      pointerEvents: 'none',
                    }}
                  />
                )}
              </Box>

              <Typography
                variant="caption"
                sx={{
                  width: '100%',
                  fontSize: '0.5625rem',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  color: sectionLabelColor,
                  fontWeight: isActive ? 600 : 400,
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {section.percent}%
              </Typography>
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

export default GuidanceTourSectionProgress;
